import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, ErrorNote } from "@/components/ui";
import { Labeled, Select, TextArea, TextField, joinList, splitList } from "@/components/Form";
import { tStatus } from "@/i18n";
import type { Customer } from "@/lib/types";

const SOURCES = ["lead", "referral", "walk_in", "agent", "other"];

export interface CustomerPayload {
  full_name: string;
  display_name: string | null;
  company: string | null;
  country: string | null;
  nationality: string | null;
  preferred_language: string | null;
  emails: string[];
  phones: string[];
  tags: string[];
  telegram_user_id: number | null;
  telegram_username: string | null;
  source: string | null;
  notes: string | null;
  status?: string;
}

export function CustomerForm({
  initial,
  busy,
  error,
  submitLabel,
  withStatus = false,
  onSubmit,
  onCancel,
}: {
  initial?: Partial<Customer>;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  withStatus?: boolean;
  onSubmit: (payload: CustomerPayload) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    full_name: initial?.full_name ?? "",
    display_name: initial?.display_name ?? "",
    company: initial?.company ?? "",
    country: initial?.country ?? "",
    nationality: initial?.nationality ?? "",
    preferred_language: initial?.preferred_language ?? "",
    emails: joinList(initial?.emails),
    phones: joinList(initial?.phones),
    tags: joinList(initial?.tags),
    telegram_user_id: initial?.telegram_user_id?.toString() ?? "",
    telegram_username: initial?.telegram_username ? `@${initial.telegram_username}` : "",
    source: initial?.source ?? "",
    notes: initial?.notes ?? "",
    status: initial?.status ?? "active",
  });
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });

  const tgRaw = f.telegram_user_id.trim();
  const tgValid = tgRaw === "" || /^\d+$/.test(tgRaw);

  return (
    <Card>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Labeled label={t("fields.fullName")}>
          <TextField value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.displayName")}>
          <TextField value={f.display_name} onChange={(e) => set("display_name", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.company")}>
          <TextField value={f.company} onChange={(e) => set("company", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.country")}>
          <TextField value={f.country} onChange={(e) => set("country", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.nationality")}>
          <TextField value={f.nationality} onChange={(e) => set("nationality", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.language")}>
          <TextField value={f.preferred_language} onChange={(e) => set("preferred_language", e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.emails")}>
          <TextField value={f.emails} onChange={(e) => set("emails", e.target.value)} placeholder="a@x.com, b@x.com" />
        </Labeled>
        <Labeled label={t("fields.phones")}>
          <TextField value={f.phones} onChange={(e) => set("phones", e.target.value)} placeholder="+9715…, +7…" />
        </Labeled>
        <Labeled label={t("fields.tags")}>
          <TextField value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="vip, repeat" />
        </Labeled>
        <Labeled label={t("fields.telegramId")}>
          <TextField
            value={f.telegram_user_id}
            onChange={(e) => set("telegram_user_id", e.target.value)}
            inputMode="numeric"
            placeholder="6712022714"
          />
          {!tgValid && (
            <span className="mt-1 block text-xs text-gold">{t("customers.telegramIdHint")}</span>
          )}
        </Labeled>
        <Labeled label={t("fields.telegramUsername")}>
          <TextField
            value={f.telegram_username}
            onChange={(e) => set("telegram_username", e.target.value)}
            placeholder="@username"
          />
        </Labeled>
        <Labeled label={t("fields.source")}>
          <Select value={f.source} onChange={(e) => set("source", e.target.value)}>
            <option value="">{t("common.none")}</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>{tStatus(s)}</option>
            ))}
          </Select>
        </Labeled>
        {withStatus && (
          <Labeled label={t("fields.status")}>
            <Select value={f.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">{tStatus("active")}</option>
              <option value="archived">{tStatus("archived")}</option>
            </Select>
          </Labeled>
        )}
      </div>
      <div className="mt-3">
        <Labeled label={t("fields.notes")}>
          <TextArea value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </Labeled>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          loading={busy}
          disabled={!f.full_name.trim() || !tgValid}
          onClick={() =>
            onSubmit({
              full_name: f.full_name.trim(),
              display_name: f.display_name || null,
              company: f.company || null,
              country: f.country || null,
              nationality: f.nationality || null,
              preferred_language: f.preferred_language || null,
              emails: splitList(f.emails),
              phones: splitList(f.phones),
              tags: splitList(f.tags),
              telegram_user_id: tgRaw ? Number(tgRaw) : null,
              telegram_username: f.telegram_username.trim().replace(/^@/, "") || null,
              source: f.source || null,
              notes: f.notes || null,
              ...(withStatus ? { status: f.status } : {}),
            })
          }
        >
          {submitLabel}
        </Button>
        <button className="text-sm text-sand-muted hover:text-sand" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </Card>
  );
}
